<template>
  <div class="stock-transfer">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 5">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Type</label>
          <FormDropdown v-model="filters.direction" :options="directionOptions" placeholder="Out & in" clearable />
        </div>
        <div class="rp-filter">
          <label>Other store</label>
          <FormDropdown v-model="filters.other_store" :options="otherStoreOptions" placeholder="All stores" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search name, code, SKU or barcode"
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-truck-delivery"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Transfer Out</div>
          <div class="rp-card-value text-danger">−{{ formatQty(summary.out_qty) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.out_value) }} · {{ plural(summary.out_count, 'transfer') }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-truck-loading"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Transfer In</div>
          <div class="rp-card-value text-success">+{{ formatQty(summary.in_qty) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.in_value) }} · {{ plural(summary.in_count, 'transfer') }}</div>
        </div>
      </div>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-scale"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Net Change</div>
          <div class="rp-card-value" :class="netClass(summary.net_qty)">{{ signed(summary.net_qty) }}</div>
          <div class="rp-card-sub">{{ signedMoney(summary.net_value) }} in stock value</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-arrows-exchange"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Transfers</div>
          <div class="rp-card-value">{{ formatInt(summary.transfer_count) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.item_count, 'item') }} · {{ plural(summary.store_count, 'store') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Transfers {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot in"></i>In</span>
            <span><i class="rp-dot out"></i>Out</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No transfers in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By store</div>
        <ul v-if="summary.by_store.length" class="rp-ranked st-stores">
          <li v-for="store in summary.by_store" :key="store.store_id || 0">
            <span class="rp-rank"><i class="ti ti-building-store"></i></span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate fw-medium" :title="store.store">{{ store.store }}</div>
              <small class="text-muted">
                {{ formatMoney(Number(store.out_value) + Number(store.in_value)) }} ·
                {{ plural(store.out_count + store.in_count, 'transfer') }}
              </small>
            </div>
            <span class="rp-chip out" title="Sent to this store">→ {{ formatQty(store.out_qty) }}</span>
            <span class="rp-chip in" title="Received from this store">← {{ formatQty(store.in_qty) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No transfers in this period.</p>
        <div class="rp-note">→ sent to · ← received from {{ currentStore }}</div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top items transferred</div>
        <ul v-if="summary.top_items.length" class="rp-ranked">
          <li v-for="(item, index) in summary.top_items" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <span class="flex-grow-1 text-truncate" :title="item.name">{{ item.name }}</span>
            <span v-if="Number(item.out_qty)" class="rp-chip out">−{{ formatQty(item.out_qty) }}</span>
            <span v-if="Number(item.in_qty)" class="rp-chip in">+{{ formatQty(item.in_qty) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No transfers in this period.</p>
      </div>
    </div>

    <!-- Transfers table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="fw-semibold text-body">
          <i class="ti ti-list-details me-1"></i>Transfers of {{ currentStore }}
          <span class="rp-pill-count ms-1">{{ formatInt(rows.length) }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button
                    type="button"
                    class="rp-expand"
                    :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                    @click="toggleRow(row.key)"
                  >
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">{{ formatDate(row.date) }}</td>
                <td class="fw-semibold text-nowrap">{{ row.voucher_id }}</td>
                <td>
                  <span class="rp-type" :class="row.direction">
                    <i class="ti me-1" :class="row.direction === 'out' ? 'ti-arrow-up-right' : 'ti-arrow-down-left'"></i>{{ row.type_label }}
                  </span>
                </td>
                <td :class="{ 'fw-semibold': row.direction === 'out' }">{{ row.from_store }}</td>
                <td>
                  <i class="ti ti-arrow-narrow-right text-muted me-1"></i>
                  <span :class="{ 'fw-semibold': row.direction === 'in' }">{{ row.to_store }}</span>
                </td>
                <td class="text-center"><span class="rp-count">{{ row.item_count }}</span></td>
                <td class="text-end fw-semibold" :class="row.direction === 'out' ? 'text-danger' : 'text-success'">
                  {{ row.direction === 'out' ? '−' : '+' }}{{ formatQty(row.qty) }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.value) }}</td>
                <td class="text-muted">{{ row.created_by || '—' }}</td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title d-flex flex-wrap justify-content-between gap-2">
                      <span>
                        <i class="ti ti-route me-1"></i>{{ row.from_store }}
                        <i class="ti ti-arrow-narrow-right mx-1"></i>{{ row.to_store }}
                      </span>
                      <span v-if="row.narration" class="text-muted fw-normal">{{ row.narration }}</span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th>Warehouse</th>
                            <th>Batch</th>
                            <th class="text-end">Quantity</th>
                            <th class="text-end">Unit Cost</th>
                            <th class="text-end">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>
                              {{ line.item_name }}
                              <small v-if="line.sku" class="d-block text-muted">SKU: {{ line.sku }}</small>
                            </td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td>{{ line.warehouse }}</td>
                            <td>{{ line.batch_no || '—' }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small>
                            </td>
                            <td class="text-end">{{ formatMoney(line.unit_cost) }}</td>
                            <td class="text-end">{{ formatMoney(line.value) }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colspan="5" class="text-end">Transfer total</th>
                            <th class="text-end">{{ formatQty(row.qty) }}</th>
                            <th></th>
                            <th class="text-end">{{ formatMoney(row.value) }}</th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-truck"></i>
                  <div class="fw-semibold">No transfers found</div>
                  <small class="text-muted">Try another period, store or search.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="5" class="text-end">Total ({{ plural(rows.length, 'transfer') }})</th>
              <th class="text-center">{{ formatInt(summary.item_count) }}</th>
              <th class="text-end">
                <span v-if="Number(summary.out_qty)" class="d-block text-danger">−{{ formatQty(summary.out_qty) }}</span>
                <span v-if="Number(summary.in_qty)" class="d-block text-success">+{{ formatQty(summary.in_qty) }}</span>
              </th>
              <th class="text-end">
                <span v-if="Number(summary.out_qty)" class="d-block">{{ formatMoney(summary.out_value) }}</span>
                <span v-if="Number(summary.in_qty)" class="d-block">{{ formatMoney(summary.in_value) }}</span>
              </th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from './inventoryReportMixin';
import periodReport, { defaultPeriod } from './periodReportMixin';

const EMPTY_SUMMARY = {
  out_qty: 0, out_value: 0, in_qty: 0, in_value: 0, out_count: 0, in_count: 0, net_qty: 0, net_value: 0,
  transfer_count: 0, item_count: 0, store_count: 0, by_store: [], trend_unit: 'day', trend: [], top_items: [],
};

const DIRECTION_OPTIONS = [
  { id: 'out', text: 'Transfer out' },
  { id: 'in', text: 'Transfer in' },
];

export default {
  name: 'StockTransferReport',
  mixins: [inventoryReport, periodReport],

  reportUrl: apiUrls.stock_transfer_report.get,
  fileName: 'stock_transfer',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'voucher_id', label: 'Voucher' },
    { key: 'type_label', label: 'Type' },
    { key: 'from_store', label: 'From (source)' },
    { key: 'to_store', label: 'To (destination)' },
    { key: 'item_count', label: 'Items', class: 'text-center', numeric: true },
    { key: 'qty', label: 'Quantity', class: 'text-end', numeric: true },
    { key: 'value', label: 'Value', class: 'text-end', numeric: true },
    { key: 'created_by', label: 'By' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), direction: null, other_store: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      directionOptions: DIRECTION_OPTIONS,
      sortKey: 'date',
      sortDir: 'desc',
    };
  },

  computed: {
    otherStoreOptions() {
      return (this.options.other_stores || []).map((s) => ({ id: s.id, text: s.name }));
    },

    currentStore() {
      return this.optionName(this.options.stores, this.storeId, 'this store');
    },

    hasActiveFilters() {
      return this.periodChanged || this.itemFiltersActive || !!this.filters.direction || !!this.filters.other_store;
    },

    visibleRows() {
      return this.sortRows(this.rows);
    },
  },

  watch: {
    'filters.direction'() { this.onFilterChange(); },
    'filters.other_store'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { direction, other_store: otherStore } = this.filters;
      return {
        ...this.periodParams(),
        ...(direction ? { direction } : {}),
        ...(otherStore ? { other_store: otherStore } : {}),
      };
    },

    resetExtraFilters() {
      return { ...defaultPeriod(this.today), direction: null, other_store: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawTrendChart(this.$refs.trendChart, 'Transfer in', 'Transfer out'));
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => {
        const items = r.lines.map((l) => `${escapeHtml(l.item_name)}${l.variant ? ` (${escapeHtml(l.variant)})` : ''} × ${this.formatQty(l.qty)}`).join('<br>');
        const sign = r.direction === 'out' ? '−' : '+';
        return `
        <tr>
          <td>${this.formatDate(r.date)}</td>
          <td>${escapeHtml(r.voucher_id)}</td>
          <td class="${r.direction}">${escapeHtml(r.type_label)}</td>
          <td>${escapeHtml(r.from_store)}</td>
          <td>${escapeHtml(r.to_store)}</td>
          <td><small>${items}</small></td>
          <td class="num ${r.direction}">${sign}${this.formatQty(r.qty)}</td>
          <td class="num">${this.formatMoney(r.value)}</td>
        </tr>`;
      }).join('');
      const table = `<table><thead><tr><th>Date</th><th>Voucher</th><th>Type</th><th>From</th><th>To</th>
          <th>Items</th><th class="num">Quantity</th><th class="num">Value</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="6" class="num">Out / In</td>
          <td class="num">−${this.formatQty(s.out_qty)} / +${this.formatQty(s.in_qty)}</td>
          <td class="num">${this.formatMoney(s.out_value)} / ${this.formatMoney(s.in_value)}</td>
        </tr></tfoot></table>`;
      const otherStore = this.optionName(this.options.other_stores || [], this.filters.other_store, 'All');
      const type = DIRECTION_OPTIONS.find((o) => o.id === this.filters.direction)?.text || 'All';
      this.openPrint(
        'Stock Transfer',
        [
          [this.periodText(), `Type: ${type}`, `Other store: ${otherStore}`, ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Transfer Out', `${this.formatQty(s.out_qty)} · ${this.formatMoney(s.out_value)}`],
          ['Transfer In', `${this.formatQty(s.in_qty)} · ${this.formatMoney(s.in_value)}`],
          ['Net Change', `${this.signed(s.net_qty)} · ${this.signedMoney(s.net_value)}`],
          ['Transfers', `${this.formatInt(s.transfer_count)} · ${this.plural(s.item_count, 'item')}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>

<style scoped>
.st-stores .rp-rank {
  font-size: 0.85rem;
}
</style>
